import { supabase } from '../../config/supabase';
import { UserProfile, UserRole } from '../../types';
import { isParentProfileDobLocked, validateParentDateOfBirth } from '../../utils/parentDobPolicy';
import {
  mergeCoachFieldsIntoProfile,
  mergeParentFieldsIntoProfile,
  pickCoachProfileUpdates,
  pickCoreProfileUpdates,
  pickParentProfileUpdates,
} from '../../utils/profileRoleFields';

async function fetchCoachExtension(userId: string): Promise<Record<string, unknown> | null> {
  const { data, error } = await supabase
    .from('coach_profiles')
    .select('institution, coach_title, coach_verified, coaching_certifications')
    .eq('profile_id', userId)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching coach_profiles:', error);
  }
  return data as Record<string, unknown> | null;
}

async function fetchParentExtension(userId: string): Promise<Record<string, unknown> | null> {
  const { data, error } = await supabase
    .from('parent_profiles')
    .select(
      'child_name, child_grad_year, child_high_school, relationship, stripe_identity_verification_session_id, stripe_identity_status, stripe_identity_verified_at, stripe_identity_last_error'
    )
    .eq('profile_id', userId)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching parent_profiles:', error);
  }
  return data as Record<string, unknown> | null;
}

async function upsertCoachExtension(
  userId: string,
  updates: Record<string, unknown>
): Promise<boolean> {
  if (Object.keys(updates).length === 0) return true;

  const { error: insertError } = await supabase
    .from('coach_profiles')
    .upsert({ profile_id: userId, ...updates }, { onConflict: 'profile_id' });

  if (insertError) {
    console.error('Error upserting coach_profiles:', insertError);
    return false;
  }
  return true;
}

async function upsertParentExtension(
  userId: string,
  updates: Record<string, unknown>
): Promise<boolean> {
  if (Object.keys(updates).length === 0) return true;

  const { error: insertError } = await supabase
    .from('parent_profiles')
    .upsert({ profile_id: userId, ...updates }, { onConflict: 'profile_id' });

  if (insertError) {
    console.error('Error upserting parent_profiles:', insertError);
    return false;
  }
  return true;
}

function isParentRole(profile: UserProfile | null | undefined): boolean {
  return profile?.user_role === UserRole.Parent;
}

export const profileService = {
  async getProfile(userId: string): Promise<UserProfile | null> {
    try {
      if (!userId) {
        return null;
      }
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        if (error.code !== 'PGRST116') {
          console.error('Error fetching profile:', error);
        }
        return null;
      }
      if (!data) return null;

      let profile = data as unknown as UserProfile;
      const role = profile.user_role;

      if (role === UserRole.Coach) {
        const coachRow = await fetchCoachExtension(userId);
        profile = mergeCoachFieldsIntoProfile(profile, coachRow);
      } else if (role === UserRole.Parent) {
        const parentRow = await fetchParentExtension(userId);
        profile = mergeParentFieldsIntoProfile(profile, parentRow);
      }

      return profile;
    } catch (error) {
      console.error('Error in getProfile:', error);
      return null;
    }
  },

  async updateProfile(userId: string, updates: Partial<UserProfile>): Promise<boolean> {
    try {
      const existing = await this.getProfile(userId);
      if (!existing) {
        console.error('Error updating profile: profile not found');
        return false;
      }

      if ('date_of_birth' in updates && isParentRole(existing)) {
        if (isParentProfileDobLocked(existing)) {
          console.error(
            'Error updating profile: date of birth is locked after identity verification'
          );
          return false;
        }

        const dob = updates.date_of_birth;
        if (dob == null || String(dob).trim() === '') {
          console.error('Error updating profile: date of birth cannot be cleared');
          return false;
        }

        const validation = validateParentDateOfBirth(String(dob));
        if (validation.ok === false) {
          console.error('Error updating profile:', validation.message);
          return false;
        }
      }

      const coachUpdates = pickCoachProfileUpdates(updates);
      const parentUpdates = pickParentProfileUpdates(updates);
      const coreUpdates = pickCoreProfileUpdates(updates);

      if ('first_name' in coreUpdates || 'last_name' in coreUpdates) {
        let firstName = coreUpdates.first_name;
        let lastName = coreUpdates.last_name;
        if (firstName === undefined) firstName = existing.first_name ?? null;
        if (lastName === undefined) lastName = existing.last_name ?? null;
        const combined = [firstName?.trim(), lastName?.trim()].filter(Boolean).join(' ');
        coreUpdates.full_name = combined || null;
      }

      const role = existing.user_role;
      const isCoach = role === UserRole.Coach;
      const isParent = isParentRole(existing);

      if (isCoach && Object.keys(coachUpdates).length > 0) {
        const ok = await upsertCoachExtension(userId, coachUpdates as Record<string, unknown>);
        if (!ok) return false;
      }

      if (isParent && Object.keys(parentUpdates).length > 0) {
        const ok = await upsertParentExtension(userId, parentUpdates as Record<string, unknown>);
        if (!ok) return false;
      }

      const legacyPayload = {
        ...coreUpdates,
        ...(isCoach ? coachUpdates : {}),
        ...(isParent ? parentUpdates : {}),
      } as Partial<UserProfile>;

      if (Object.keys(legacyPayload).length === 0) {
        return true;
      }

      const { error } = await supabase.from('profiles').update(legacyPayload).eq('id', userId);

      if (error) {
        console.error('Error updating profile:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateProfile:', error);
      return false;
    }
  },

  async createProfile(userId: string, email: string): Promise<boolean> {
    try {
      const { error } = await supabase.from('profiles').insert([{ id: userId, email }]);

      if (error) {
        console.error('Error creating profile:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in createProfile:', error);
      return false;
    }
  },

  async profileExists(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        if (error.code !== 'PGRST116') {
          console.error('Error checking profile existence:', error);
        }
        return false;
      }

      return Boolean(data);
    } catch (error) {
      console.error('Error in profileExists:', error);
      return false;
    }
  },

  async checkUserExistence(email: string): Promise<{ exists: boolean; email: string }> {
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    const apiUrl = isLocalhost
      ? 'http://localhost:3001/api/auth/check-user-exists'
      : '/api/auth/check-user-exists';

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const message = (errorBody && errorBody.error) || 'Failed to check user existence';
      throw new Error(message);
    }

    return response.json();
  },
};
